import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import moment from 'moment';

import { FHAntdDateTimePicker, FHSelect, FHSmartTextarea } from '../../../../base';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import { getRangeArray } from '../../../../../utils/common';
import { Themes, BackgroundTypes } from '../../../../../constants';
import { GoalTypes, TreatmentPlanGoalStatus } from './constants';
import { FORTA_YELLOW } from '../../../../../utils/colors';
import { targetDescriptionBlacklist } from '../../../../../pages/constants';

import type { ITreatmentPlanGoal, SelectValueType } from '../../../../../utils/types';
import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';

interface DescriptionSectionProps {
  goal: ITreatmentPlanGoal;
  errors: any;
  onUpdateField: (key: string, value: any) => void;
}

function DescriptionSection(props: DescriptionSectionProps) {
  const { goal, errors, onUpdateField } = props;
  const isTpEditable = useTreatmentPlanEditable();
  const [description, setDescription] = useState<string>(goal?.description || '');
  const [operationalDefinition, setOperationalDefinition] = useState<string>(
    goal?.operational_definition || '',
  );
  const [objective, setObjective] = useState<string>(goal?.objective || '');
  const [antecedentModifications, setAntecedentModifications] = useState<string>(
    goal?.antecedent_modifications || '',
  );
  const [consequenceModifications, setConsequenceModifications] = useState<string>(
    goal?.consequence_modifications || '',
  );
  const [errorState, setErrorState] = useState<any>(null);
  const [isEditable, setIsEditable] = useState<boolean>(false);

  useEffect(() => {
    setErrorState(errors);
  }, [errors]);

  useEffect(() => {
    if (!goal) return;

    setIsEditable(isTpEditable && goal.is_active);
    setDescription(goal?.description || '');
    setOperationalDefinition(goal?.operational_definition || '');
    setObjective(goal?.objective || '');
    setAntecedentModifications(goal?.antecedent_modifications || '');
    setConsequenceModifications(goal?.consequence_modifications || '');
  }, [isTpEditable, goal]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography fontWeight={'bold'} mr={1}>
            Date of introduction:*
          </Typography>
          <FHAntdDateTimePicker
            data-testid="goalIntroductionDatePicker"
            format="MM/YY"
            picker={'month'}
            allowClear={false}
            colorTheme={Themes.Primary}
            coloredBackground
            inputHeight={40}
            sx={{ minWidth: 150 }}
            disabled={!isEditable}
            value={moment(goal.date_introduced)}
            onChange={(value: Moment) =>
              onUpdateField('date_introduced', value.format('YYYY-MM-DD'))
            }
            onSelect={(value: Moment) =>
              onUpdateField('date_introduced', value.format('YYYY-MM-DD'))
            }
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography fontWeight={'bold'} mr={1}>
            Status:*
          </Typography>
          <FHSelect
            sx={{ minWidth: 150 }}
            inputHeight={40}
            colorTheme={Themes.Primary}
            backgroundType={BackgroundTypes.Colored}
            disabled={!isEditable}
            options={Object.entries(TreatmentPlanGoalStatus)
              .filter(([label]) => label !== 'New goal')
              .map(([label, value]) => ({ value, label }))}
            value={goal.status_at_creation || TreatmentPlanGoalStatus['Not introduced']}
            onUpdate={(value: SelectValueType) => onUpdateField('status_at_creation', value)}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography fontWeight={'bold'} mr={1}>
            Baseline level:*
          </Typography>
          <FHSelect
            sx={{ minWidth: 150 }}
            inputHeight={40}
            colorTheme={Themes.Primary}
            backgroundType={BackgroundTypes.Colored}
            disabled={!isEditable}
            options={[
              { value: 'will_determine', label: 'Will determine' },
              ...getRangeArray(0, 100).map((num) => ({ value: num, label: num })),
            ]}
            value={
              goal.baseline_at_creation === null ? 'will_determine' : goal.baseline_at_creation
            }
            onUpdate={(value: SelectValueType) => onUpdateField('baseline_at_creation', value)}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography fontWeight={'bold'} mr={1}>
            Current level:*
          </Typography>
          <FHSelect
            sx={{ minWidth: 150 }}
            inputHeight={40}
            colorTheme={Themes.Primary}
            backgroundType={BackgroundTypes.Colored}
            disabled={!isEditable}
            options={[
              { value: 'will_determine', label: 'Will determine' },
              ...getRangeArray(0, 100).map((num) => ({ value: num, label: num })),
            ]}
            value={
              goal.current_level_at_creation === null
                ? 'will_determine'
                : goal.current_level_at_creation
            }
            onUpdate={(value: SelectValueType) => onUpdateField('current_level_at_creation', value)}
          />
        </Box>
      </Box>
      {goal.goal_type === GoalTypes.BehaviorReduction ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            columnGap: 3,
            rowGap: 2,
            mt: 2,
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', rowGap: 1 }}>
            <Typography fontWeight={'bold'} textAlign={'left'}>
              Operational definition:*
            </Typography>
            <FHSmartTextarea
              id={`goal_operational_definition-${goal.id}`}
              colorTheme={Themes.Primary}
              coloredBackground
              hightlightOnDetection
              heightlightColour={FORTA_YELLOW}
              blackListedWordsPhrases={targetDescriptionBlacklist}
              maxRows={2}
              minLength={10}
              disabled={!isEditable}
              expandable={isEditable}
              value={operationalDefinition}
              onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                setOperationalDefinition(e.target.value);
                if (e.target.value.length < 10) {
                  setErrorState((prev: any) => ({
                    ...prev,
                    operational_definition: true,
                  }));
                } else {
                  setErrorState((prev: any) => ({
                    ...prev,
                    operational_definition: false,
                  }));
                }
              }}
              onBlur={(e) => {
                onUpdateField('operational_definition', e.target.value);
              }}
              error={errorState && errorState['operational_definition']}
              helperText={
                errorState && errorState['operational_definition'] ? 'Minimum 10 characters' : ''
              }
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', rowGap: 1 }}>
            <Typography fontWeight={'bold'} textAlign={'left'}>
              Objective:*
            </Typography>
            <FHSmartTextarea
              id={`goal-objective-${goal.id}`}
              colorTheme={Themes.Primary}
              coloredBackground
              hightlightOnDetection
              heightlightColour={FORTA_YELLOW}
              blackListedWordsPhrases={targetDescriptionBlacklist}
              maxRows={2}
              minLength={10}
              disabled={!isEditable}
              expandable={isEditable}
              value={objective}
              onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                setObjective(e.target.value);
                if (e.target.value.length < 10) {
                  setErrorState((prev: any) => ({
                    ...prev,
                    objective: true,
                  }));
                } else {
                  setErrorState((prev: any) => ({
                    ...prev,
                    objective: false,
                  }));
                }
              }}
              onBlur={(e) => {
                onUpdateField('objective', e.target.value);
              }}
              error={errorState && errorState['objective']}
              helperText={errorState && errorState['objective'] ? 'Minimum 10 characters' : ''}
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', rowGap: 1 }}>
            <Typography fontWeight={'bold'} textAlign={'left'}>
              Antecedent modifications:*
            </Typography>
            <FHSmartTextarea
              id={`tp-antecedent-modifications-${goal.id}`}
              colorTheme={Themes.Primary}
              coloredBackground
              hightlightOnDetection
              heightlightColour={FORTA_YELLOW}
              blackListedWordsPhrases={targetDescriptionBlacklist}
              maxRows={2}
              minLength={10}
              disabled={!isEditable}
              expandable={isEditable}
              value={antecedentModifications}
              onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                setAntecedentModifications(e.target.value);
                if (e.target.value.length < 10) {
                  setErrorState((prev: any) => ({
                    ...prev,
                    antecedent_modifications: true,
                  }));
                } else {
                  setErrorState((prev: any) => ({
                    ...prev,
                    antecedent_modifications: false,
                  }));
                }
              }}
              onBlur={(e) => {
                onUpdateField('antecedent_modifications', e.target.value);
              }}
              error={errorState && errorState['antecedent_modifications']}
              helperText={
                errorState && errorState['antecedent_modifications'] ? 'Minimum 10 characters' : ''
              }
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', rowGap: 1 }}>
            <Typography fontWeight={'bold'} textAlign={'left'}>
              Consequence modifications:*
            </Typography>
            <FHSmartTextarea
              id={`goal-consequence-modifications-${goal.id}`}
              colorTheme={Themes.Primary}
              coloredBackground
              hightlightOnDetection
              heightlightColour={FORTA_YELLOW}
              blackListedWordsPhrases={targetDescriptionBlacklist}
              maxRows={2}
              minLength={10}
              disabled={!isEditable}
              expandable={isEditable}
              value={consequenceModifications}
              onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                setConsequenceModifications(e.target.value);
                if (e.target.value.length < 10) {
                  setErrorState((prev: any) => ({
                    ...prev,
                    consequence_modifications: true,
                  }));
                } else {
                  setErrorState((prev: any) => ({
                    ...prev,
                    consequence_modifications: false,
                  }));
                }
              }}
              onBlur={(e) => {
                onUpdateField('consequence_modifications', e.target.value);
              }}
              error={errorState && errorState['consequence_modifications']}
              helperText={
                errorState && errorState['consequence_modifications'] ? 'Minimum 10 characters' : ''
              }
            />
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
          <Typography fontWeight={'bold'} textAlign={'left'}>
            Description:*
          </Typography>
          <FHSmartTextarea
            id={`goal_description-${goal.id}`}
            colorTheme={Themes.Primary}
            coloredBackground
            hightlightOnDetection
            heightlightColour={FORTA_YELLOW}
            blackListedWordsPhrases={targetDescriptionBlacklist}
            maxRows={2}
            disabled={!isEditable}
            expandable={isEditable}
            value={description}
            onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
              setDescription(e.target.value);
              if (e.target.value.length < 10) {
                setErrorState((prev: any) => ({
                  ...prev,
                  description: true,
                }));
              } else {
                setErrorState((prev: any) => ({
                  ...prev,
                  description: false,
                }));
              }
            }}
            onBlur={(e) => {
              onUpdateField('description', e.target.value);
            }}
            error={errorState && errorState['description']}
            helperText={errorState && errorState['description'] ? 'Minimum 10 characters' : ''}
          />
        </Box>
      )}
      {goal.goal_type === GoalTypes.BehaviorReduction && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
            maxWidth: 360,
          }}
        >
          <Typography fontWeight={'bold'}>Target mastery (Months):*</Typography>
          <FHSelect
            sx={{ minWidth: 150 }}
            inputHeight={40}
            colorTheme={Themes.Primary}
            backgroundType={BackgroundTypes.Colored}
            disabled={!isEditable}
            options={getRangeArray(1, 12).map((num) => ({
              value: num,
              label: num,
            }))}
            value={goal?.target_mastery || ''}
            onUpdate={(value: SelectValueType) => {
              const targetMastery = Number(value);
              if (Number.isInteger(targetMastery) && targetMastery >= 1 && targetMastery <= 12) {
                onUpdateField('target_mastery', targetMastery);
              }
            }}
            error={errors && errors['target_mastery']}
          />
        </Box>
      )}
    </Box>
  );
}

export default DescriptionSection;
